import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  ToggleButtonGroup,
  Button,
  Tooltip,
} from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CropIcon from '@mui/icons-material/Crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PDFViewer from './PDFViewer';
import ImageRegionSelector from './ImageRegionSelector';

const DocumentViewerDrawer = ({
  open,
  onClose,
  document = null,
  onTextQuote,
  onRegionCrop,
  inline = false,
  noWrapper = false,
}) => {
  const [mode, setMode] = useState('text'); // 'text' | 'region'
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const activeDocument = document;

  // Reset state when active document changes
  useEffect(() => {
    setCurrentPage(1);
    setZoom(100);
    setMode('text');
  }, [activeDocument?.id, activeDocument?.url]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        setCurrentPage((p) => p + 1);
      } else if ((e.metaKey || e.ctrlKey) && e.key === '=') {
        e.preventDefault();
        setZoom((z) => Math.min(z + 25, 300));
      } else if ((e.metaKey || e.ctrlKey) && e.key === '-') {
        e.preventDefault();
        setZoom((z) => Math.max(z - 25, 25));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentPage, totalPages, onClose]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 300));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 25));

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handleTextQuote = useCallback(
    (selection) => {
      if (onTextQuote && activeDocument) {
        onTextQuote({
          text: selection.text,
          documentName: activeDocument.name || 'Document',
          page: currentPage,
          documentId: activeDocument.id,
        });
      }
    },
    [onTextQuote, activeDocument, currentPage]
  );

  const handleRegionCrop = useCallback(
    (cropData) => {
      if (onRegionCrop && activeDocument) {
        onRegionCrop({
          imageBlob: cropData.blob,
          imageDataUrl: cropData.dataUrl,
          documentName: activeDocument.name || 'Document',
          page: currentPage,
          documentId: activeDocument.id,
          bounds: cropData.bounds,
        });
      }
    },
    [onRegionCrop, activeDocument, currentPage]
  );

  const getDocumentType = (doc) => {
    if (!doc) return null;
    if (doc.type) return doc.type;
    const name = doc.name || doc.path || '';
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
    if (['docx', 'doc'].includes(ext)) return 'docx';
    if (['xlsx', 'xls'].includes(ext)) return 'xlsx';
    return 'unknown';
  };

  const documentType = getDocumentType(activeDocument);
  const isPDF = documentType === 'pdf';
  const isImage = documentType === 'image';

  const innerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* Header: document name + zoom + page nav + close */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.level1',
          minHeight: 40,
        }}
      >
        <Typography
          level="title-sm"
          noWrap
          sx={{ flex: 1, minWidth: 0 }}
        >
          {activeDocument?.name || 'Document'}
        </Typography>

        {/* Mode Toggle */}
        <ToggleButtonGroup
          size="sm"
          value={mode}
          onChange={(e, newMode) => newMode && setMode(newMode)}
          sx={{ mx: 0.5 }}
        >
          <Tooltip title="Text Selection Mode" placement="bottom">
            <Button value="text" variant={mode === 'text' ? 'solid' : 'outlined'}>
              <TextFieldsIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Region Selection Mode" placement="bottom">
            <Button value="region" variant={mode === 'region' ? 'solid' : 'outlined'}>
              <CropIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </ToggleButtonGroup>

        {/* Zoom Controls */}
        <IconButton size="sm" variant="plain" onClick={handleZoomOut} disabled={zoom <= 25}>
          <ZoomOutIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography level="body-xs" sx={{ minWidth: 35, textAlign: 'center' }}>
          {zoom}%
        </Typography>
        <IconButton size="sm" variant="plain" onClick={handleZoomIn} disabled={zoom >= 300}>
          <ZoomInIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {/* Page Navigation (PDF only) */}
        {isPDF && (
          <>
            <IconButton size="sm" variant="plain" onClick={handlePrevPage} disabled={currentPage <= 1}>
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography level="body-xs" sx={{ minWidth: 50, textAlign: 'center' }}>
              {currentPage} / {totalPages}
            </Typography>
            <IconButton size="sm" variant="plain" onClick={handleNextPage} disabled={currentPage >= totalPages}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}

        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={onClose}
          aria-label="Close document sidebar"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      {/* Document Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
          bgcolor: 'neutral.100',
        }}
      >
        {!activeDocument ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <Typography level="body-lg" color="neutral">
              No document selected
            </Typography>
          </Stack>
        ) : mode === 'region' ? (
          <ImageRegionSelector
            src={activeDocument.url || activeDocument.src}
            documentType={documentType}
            page={currentPage}
            zoom={zoom}
            onCrop={handleRegionCrop}
          />
        ) : isPDF ? (
          <PDFViewer
            src={activeDocument.url || activeDocument.src}
            page={currentPage}
            zoom={zoom}
            onPageChange={setCurrentPage}
            onTotalPagesChange={setTotalPages}
            onTextSelect={handleTextQuote}
          />
        ) : isImage ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100%',
              p: 2,
            }}
          >
            <Box
              component="img"
              src={activeDocument.url || activeDocument.src}
              alt={activeDocument.name || 'Document'}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        ) : (
          // Fallback for docx, xlsx, etc. - use Google Docs Viewer
          <Box sx={{ width: '100%', height: '100%' }}>
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                activeDocument.url || activeDocument.src
              )}&embedded=true`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Document Viewer"
            />
          </Box>
        )}
      </Box>

    </Box>
  );

  if (noWrapper) {
    return innerContent;
  }

  if (inline) {
    return (
      <Box
        aria-hidden={!open}
        sx={{
          flexBasis: open ? { xs: '100%', md: '50vw' } : { xs: '0px', md: '0px' },
          width: open ? { xs: '100%', md: '50vw' } : { xs: '0px', md: '0px' },
          minWidth: 0,
          maxWidth: open ? { xs: '100%', md: '900px' } : { xs: '0px', md: '0px' },
          opacity: open ? 1 : 0,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          pointerEvents: open ? 'auto' : 'none',
          borderLeftStyle: 'solid',
          borderLeftWidth: open ? '1px' : '0px',
          borderColor: 'divider',
          bgcolor: 'background.surface',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flexShrink: 0,
          overflow: 'hidden',
          willChange: 'flex-basis, width, max-width, transform, opacity',
          transition:
            'flex-basis 380ms cubic-bezier(0.4, 0, 0.2, 1), width 380ms cubic-bezier(0.4, 0, 0.2, 1), max-width 380ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease, transform 380ms cubic-bezier(0.4, 0, 0.2, 1), border-left-width 380ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {innerContent}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        content: {
          sx: {
            width: { xs: '100vw', md: '50vw' },
            minWidth: { md: 500 },
            maxWidth: { md: 900 },
            bgcolor: 'background.surface',
            boxShadow: 'xl',
            transition: 'transform 0.3s ease-out',
          },
        },
        backdrop: {
          sx: {
            backdropFilter: 'blur(2px)',
          },
        },
      }}
    >
      {innerContent}
    </Drawer>
  );
};

export default DocumentViewerDrawer;
